import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { catchError, filter, map, of, switchMap, take } from 'rxjs';
import { TrialOfferDialogComponent } from 'src/app/components/trial-offer-dialog/trial-offer-dialog.component';
import { TRIAL_OFFER_QUERY_PARAM } from 'src/app/constants/trial-offer.constants';
import { BillingService } from 'src/app/services/billing/billing.service';
import { AuthSelectors } from 'src/app/store/auth/auth.selectors';

@Injectable({ providedIn: 'root' })
export class TrialOfferDeepLinkService {
  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly store: Store,
    private readonly dialog: MatDialog,
    private readonly billingService: BillingService,
  ) {}

  init(): void {
    this.route.queryParams
      .pipe(
        map(params => params[TRIAL_OFFER_QUERY_PARAM]),
        filter((offerKey): offerKey is string => !!offerKey),
        take(1),
        switchMap(offerKey => this.whenBillingDetailsLoaded().pipe(map(() => offerKey))),
      )
      .subscribe(offerKey => this.open(offerKey));
  }

  // The billing interceptor reads the company id from the store, so the request must wait for it.
  // A failed load still opens the dialog: it has a state for users without billing access.
  private whenBillingDetailsLoaded() {
    return this.store.select(AuthSelectors.company).pipe(
      filter(company => !!company?.id),
      take(1),
      switchMap(() => this.billingService.getBillingDetails('no-cache')),
      catchError(() => of(null)),
    );
  }

  private open(offerKey: string): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [TRIAL_OFFER_QUERY_PARAM]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.dialog.open(TrialOfferDialogComponent, {
      data: { offerKey },
      width: '432px',
      maxWidth: '92vw',
      autoFocus: false,
    });
  }
}
